import type { FormSpec } from './form-specs';

/** Normalised field keys the backend reads the sender's address from, in order. */
export const EMAIL_FIELD_KEYS = [
  'email',
  'emailaddress',
  'mail',
  'youremail',
  'contactemail',
  'useremail',
  'replyto',
] as const;

/** `key` lowercased with `-`, `_` and spaces removed. */
export function normalizeFieldKey(key: string): string {
  return key.toLowerCase().replace(/[-_ ]/g, '');
}

export interface TemplateVariable {
  name: string;
  description: string;
}

const EXAMPLE_VARIABLES: TemplateVariable[] = [
  { name: 'name', description: "Submitter's name" },
  { name: 'email', description: "Submitter's email address" },
  { name: 'message', description: 'Message content' },
  { name: 'company', description: 'Company/organization name' },
  { name: 'phone', description: 'Phone number' },
  { name: 'subject', description: 'Subject or topic' },
];

export type AutoReplyText = 'auto_response_subject' | 'auto_response_text';

/**
 * Inline checks before an auto-reply save, keyed like the server's field errors:
 * with auto-reply on, both the subject and the text must be written.
 */
export function autoReplyTextErrors(next: {
  enabled: boolean;
  subject?: string | null;
  text?: string | null;
}): AutoReplyText[] {
  if (!next.enabled) return [];
  const missing: AutoReplyText[] = [];
  if (!next.subject?.trim()) missing.push('auto_response_subject');
  if (!next.text?.trim()) missing.push('auto_response_text');
  return missing;
}

/**
 * Whether a field is read as the submitter's email: declared `field = "email"`,
 * or a key, `name` or `map_to` in the normalised set.
 */
export function hasEmailField(spec: FormSpec | null): boolean {
  if (!spec) return false;
  const isKey = (name: string | undefined) =>
    !!name && (EMAIL_FIELD_KEYS as readonly string[]).includes(normalizeFieldKey(name));
  return Object.entries(spec.fields).some(
    ([key, field]) =>
      field.field === 'email' || isKey(key) || isKey(field.name) || isKey(field.map_to)
  );
}

/** The fields of the spec, or common example names when the form has none. */
export function templateVariables(spec: FormSpec | null): TemplateVariable[] {
  const entries = spec ? Object.entries(spec.fields) : [];
  if (entries.length === 0) return EXAMPLE_VARIABLES;
  return entries.map(([key, field]) => ({
    name: key,
    description: field.label || `Field: ${key}`,
  }));
}

export function templateExample(spec: FormSpec | null): string {
  const hasFields = spec !== null && Object.keys(spec.fields).length > 0;
  if (!hasFields) {
    return [
      'Hello {{ name | "there" }},',
      '',
      "Thank you for reaching out! We've received your message and will get back to you shortly.",
      '',
      "If you have any urgent questions, don't hesitate to call us directly.",
      '',
      'Best regards,',
      'The Team',
    ].join('\n');
  }
  const variables = templateVariables(spec);
  const nameVar = variables[0]?.name || 'name';
  return [
    `Hello {{ ${nameVar} | "there" }},`,
    '',
    "Thank you for reaching out! We've received your submission and will review it shortly.",
    '',
    ...(variables.length > 2
      ? [
          `Here's a summary of what you sent us:`,
          ...variables.slice(1, 4).map((v) => `- ${v.description}: {{ ${v.name} }}`),
          '',
        ]
      : []),
    "We'll get back to you as soon as possible.",
    '',
    'Best regards,',
    'The Team',
  ].join('\n');
}

export const EXAMPLE_SUBJECTS = [
  'Thank you for your message, {{ name | "Valued Customer" }}',
  'Re: {{ subject | "Your inquiry" }}, confirmation',
  '{{ company | "Our Team" }}: we received your submission',
];
